import { useEffect, useState, useCallback } from 'react';
import { FileText } from 'lucide-react';
import api from '@/lib/api';

/**
 * AttachedFormsBadge — small visual chip showing how many custom-form instances
 * are attached to a record (Job / Permit / Invoice / Estimate).
 *
 * Per Alian's video: "yo necesito ver de un vistazo cuántos forms tiene cada
 * trabajo / permiso, sin tener que abrirlo".
 *
 * Props:
 *   - linkedType: 'job' | 'permit' | 'invoice' | 'estimate' | 'project'
 *   - linkedId:   string
 *   - count:      number  (optional pre-fetched count to avoid an extra request)
 *   - onClick:    () => void  (optional — open the AttachCustomForm dialog)
 *   - className:  string
 */
export default function AttachedFormsBadge({ linkedType, linkedId, count = null, onClick = null, className = '' }) {
  const [n, setN] = useState(count);

  useEffect(() => { if (count !== null) setN(count); }, [count]);

  useEffect(() => {
    if (count !== null || !linkedType || !linkedId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/custom-forms/instances/counts', {
          params: { linked_type: linkedType, linked_ids: linkedId },
        });
        if (!cancelled) setN(res.data?.counts?.[linkedId] || 0);
      } catch {
        if (!cancelled) setN(0);
      }
    })();
    return () => { cancelled = true; };
  }, [linkedType, linkedId, count]);

  if (n === null || n === 0) return null;

  const handleClick = (e) => {
    if (onClick) {
      e.stopPropagation();
      onClick();
    }
  };

  return (
    <span
      role={onClick ? 'button' : 'status'}
      onClick={handleClick}
      data-testid={`attached-forms-badge-${linkedId}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100 transition-colors ${onClick ? 'cursor-pointer' : ''} ${className}`}
      title={`${n} formulario${n === 1 ? '' : 's'} adjunto${n === 1 ? '' : 's'}`}
    >
      <FileText className="w-3 h-3" />
      {n} {n === 1 ? 'form' : 'forms'}
    </span>
  );
}

/**
 * useAttachedFormCounts — bulk-fetch counts for a list of records.
 * Returns a map { id: count } so list pages can display the badge without
 * triggering N requests.
 */
export function useAttachedFormCounts(linkedType, ids) {
  const [counts, setCounts] = useState({});

  const fetcher = useCallback(async () => {
    const filtered = (ids || []).filter(Boolean);
    if (!linkedType || filtered.length === 0) {
      setCounts({});
      return;
    }
    try {
      // Chunk ids to keep URL length under 2KB.
      const chunks = [];
      for (let i = 0; i < filtered.length; i += 80) chunks.push(filtered.slice(i, i + 80));
      const merged = {};
      for (const chunk of chunks) {
        const res = await api.get('/custom-forms/instances/counts', {
          params: { linked_type: linkedType, linked_ids: chunk.join(',') },
        });
        Object.assign(merged, res.data?.counts || {});
      }
      setCounts(merged);
    } catch {
      setCounts({});
    }
  }, [linkedType, JSON.stringify(ids || [])]);

  useEffect(() => { fetcher(); }, [fetcher]);

  return counts;
}
